import { createContext, useContext, useState, useCallback, useMemo, ReactNode, useRef } from 'react';
import { ChatSession, listSessions, deleteSession } from '../services/chatbot.service';

interface ChatSessionContextType {
  sessions: ChatSession[];
  currentSessionId: string | null;
  setCurrentSessionId: (id: string | null) => void;
  refreshSessions: () => void;
  onSelectSession: (id: string) => void;
  onNewSession: () => void;
  onDeleteSession: (id: string) => void;
  // Callbacks registered by ChatContainer; `null` al desmontarse (el chat ya no está en pantalla).
  registerCallbacks: (cbs: ChatCallbacks | null) => void;
}

interface ChatCallbacks {
  selectSession: (id: string) => void;
  newSession: () => void;
}

const ChatSessionContext = createContext<ChatSessionContextType | null>(null);

export function useChatSession() {
  return useContext(ChatSessionContext);
}

export function ChatSessionProvider({ children }: { children: ReactNode }) {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionIdState] = useState<string | null>(null);

  // Store ChatContainer callbacks (null mientras el chat no está montado, p. ej. en otra pestaña)
  const callbacksRef = useRef<ChatCallbacks | null>(null);
  // Conversación elegida en la barra lateral cuando el chat aún no está montado: se abre al montarse.
  const pendingSelectionRef = useRef<string | null>(null);

  // El chat sincroniza su conversación local (null al montar). Mientras haya una selección pendiente,
  // ese null no debe borrar la conversación elegida antes de que se cargue.
  const setCurrentSessionId = useCallback((id: string | null) => {
    if (id === null && pendingSelectionRef.current) return;
    setCurrentSessionIdState(id);
  }, []);

  const refreshSessions = useCallback(() => {
    listSessions()
      .then(setSessions)
      .catch(() => {});
  }, []);

  const registerCallbacks = useCallback((cbs: ChatCallbacks | null) => {
    callbacksRef.current = cbs;
    // El chat acaba de montarse: abre la conversación que se eligió estando en otra pestaña.
    const pending = pendingSelectionRef.current;
    if (cbs && pending) {
      pendingSelectionRef.current = null;
      cbs.selectSession(pending);
    }
  }, []);

  const onSelectSession = useCallback((id: string) => {
    setCurrentSessionIdState(id);
    if (callbacksRef.current) {
      pendingSelectionRef.current = null;
      callbacksRef.current.selectSession(id);
    } else {
      pendingSelectionRef.current = id;
    }
  }, []);

  const onNewSession = useCallback(() => {
    pendingSelectionRef.current = null;
    setCurrentSessionIdState(null);
    callbacksRef.current?.newSession();
  }, []);

  const onDeleteSession = useCallback(async (id: string) => {
    try {
      await deleteSession(id);
      if (currentSessionId === id) {
        onNewSession();
      }
      refreshSessions();
    } catch {
      // Silent fail
    }
  }, [currentSessionId, onNewSession, refreshSessions]);

  // El valor se memoiza: los consumidores no se re-renderizan por cambios ajenos y,
  // si dependen de las funciones (estables), sus efectos no se re-disparan.
  const value = useMemo(() => ({
    sessions,
    currentSessionId,
    setCurrentSessionId,
    refreshSessions,
    onSelectSession,
    onNewSession,
    onDeleteSession,
    registerCallbacks,
  }), [sessions, currentSessionId, setCurrentSessionId, refreshSessions, onSelectSession, onNewSession, onDeleteSession, registerCallbacks]);

  return (
    <ChatSessionContext.Provider value={value}>
      {children}
    </ChatSessionContext.Provider>
  );
}
